/* VSKLO Chekly passive checkout API.
   Checkout interception is owned by vsklo-checkout-guard.js.
   This asset only exposes a Chekly redirect fallback for the UA cart flow.
   It intentionally installs no checkout click/touch/submit listeners. The canonical checkout guard owns comment sanitization and handoff.
   Consent-gated GA4 client/session identifiers are forwarded only to preserve analytics attribution.
*/
(function () {
  'use strict';

  var normalizedPath = String(window.location.pathname || '/').replace(/\/+$/, '');
  if (!/(^|\/)cart$/.test(normalizedPath)) return;

  if (window.__VSKLO_CHEKLY_PASSIVE_API_V3__) return;
  window.__VSKLO_CHEKLY_PASSIVE_API_V3__ = true;

  var processing = false;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function getScript() {
    return document.querySelector('[data-chekly-url]');
  }

  function getCookie(name) {
    try {
      var pattern = new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, '\\$1') + '=([^;]*)');
      var match = document.cookie.match(pattern);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  function cookiesByPrefix(prefix) {
    var result = [];
    try {
      String(document.cookie || '').split(';').forEach(function (part) {
        var index = part.indexOf('=');
        if (index < 0) return;
        var name = clean(part.slice(0, index));
        if (name.indexOf(prefix) !== 0) return;
        var value = part.slice(index + 1);
        try { value = decodeURIComponent(value); } catch (error) {}
        result.push({ name: name, value: clean(value) });
      });
    } catch (error) {}
    return result;
  }

  function analyticsAttributionAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy) return false;
      if (typeof privacy.analyticsProcessingAllowed !== 'function') return false;
      if (typeof privacy.marketingAllowed !== 'function') return false;
      return privacy.analyticsProcessingAllowed() === true && privacy.marketingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function gaClientIdFromCookie() {
    var raw = getCookie('_ga');
    if (!raw) return '';
    var parts = raw.split('.');
    if (parts.length < 4) return '';
    var first = parts[parts.length - 2];
    var second = parts[parts.length - 1];
    if (!/^\d+$/.test(first) || !/^\d+$/.test(second)) return '';
    return (first + '.' + second).slice(0, 120);
  }

  function parseGaSessionCookie(raw) {
    raw = clean(raw);
    if (!raw) return null;

    var sessionId = '';
    var sessionNumber = '';
    var idMatch = raw.match(/(?:^|[.$])s(\d+)(?=[$.]|$)/);
    var numberMatch = raw.match(/(?:^|[.$])o(\d+)(?=[$.]|$)/);

    if (idMatch) sessionId = idMatch[1];
    if (numberMatch) sessionNumber = numberMatch[1];

    if (!sessionId) {
      var parts = raw.split('.');
      if (/^GS\d+$/.test(parts[0] || '') && /^\d+$/.test(parts[2] || '')) {
        sessionId = parts[2];
        if (/^\d+$/.test(parts[3] || '')) sessionNumber = parts[3];
      }
    }

    if (!sessionId) return null;
    return { id: sessionId, number: sessionNumber };
  }

  function gaSessionIdentity() {
    var cookies = cookiesByPrefix('_ga_');
    for (var i = 0; i < cookies.length; i += 1) {
      var parsed = parseGaSessionCookie(cookies[i].value);
      if (parsed) return parsed;
    }
    return { id: '', number: '' };
  }

  function appendGaIdentityParams(params) {
    if (!analyticsAttributionAllowed()) return;

    var clientId = gaClientIdFromCookie();
    var session = gaSessionIdentity();

    if (clientId) params.set('utm_ga_client_id', clientId);
    if (session.id) params.set('utm_ga_session_id', session.id);
    if (session.number) params.set('utm_ga_session_number', session.number);
  }

  function readJsonStorage(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function readTrackingCookie() {
    try {
      return JSON.parse(getCookie('chekly_tracking') || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function appendTrackingParams(params) {
    try {
      var query = new URLSearchParams(window.location.search || '');
      var cookieTracking = readTrackingCookie();
      var stored = readJsonStorage('vsklo_attribution_v2');
      var storedLast = stored.last || {};
      var storedFirst = stored.first || {};

      [
        'utm_campaign',
        'utm_medium',
        'utm_source',
        'utm_content',
        'utm_term',
        'utm_id',
        'fbclid',
        'ttclid',
        'epik',
        'ScCid',
        'gclid',
        'gbraid',
        'wbraid',
        'msclkid'
      ].forEach(function (key) {
        var value = query.get(key) || cookieTracking[key] || storedLast[key] || storedFirst[key] || '';
        if (value) params.set(key === 'ScCid' ? 'sccid' : key, value);
      });

      [['_fbc', '_fbc'], ['_fbp', '_fbp'], ['_ttp', 'ttp']].forEach(function (pair) {
        var value = getCookie(pair[0]);
        if (value) params.set(pair[1], value);
      });

      var linker = query.get('_gl');
      if (linker) params.set('_gl', linker);

      var shopifySa = getCookie('_shopify_sa_p');
      if (shopifySa) params.set('_shopify_sa_p', shopifySa);

      appendGaIdentityParams(params);

      var language = (navigator.languages && navigator.languages[0]) || navigator.language || '';
      if (language) params.set('utm_lang', String(language).slice(0, 2));
    } catch (error) {}
  }

  function cartToken() {
    return clean(window.__VSKLO_CART_TOKEN__) || getCookie('cart');
  }

  function processCheckout() {
    if (processing) return true;

    var script = getScript();
    var appBaseUrl = clean(
      (script && (script.getAttribute('data-checkout-domain') || script.getAttribute('data-chekly-url'))) || 'https://chekly-app.com'
    );
    var token = cartToken();
    var storefrontHost = clean(window.location.host || '');
    var shopifyDomain = clean(window.Shopify && window.Shopify.shop);
    var storeName = shopifyDomain || storefrontHost;

    if (!appBaseUrl || !token || !storeName) {
      window.location.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '') + '/checkout';
      return true;
    }

    try {
      var canonicalBuilder = window.VSKLOBuildCheklyCheckoutUrl;
      var checkoutUrl = typeof canonicalBuilder === 'function' ? canonicalBuilder(null) : null;

      // Compatibility-only fallback if the canonical guard was not loaded.
      if (!checkoutUrl) {
        checkoutUrl = new URL('/checkout/', appBaseUrl);
        var rootRoute = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
        var theme = clean(script && script.getAttribute('data-chekly-theme')) || 'chekly';

        checkoutUrl.searchParams.set('storeName', storeName);
        checkoutUrl.searchParams.set('cartToken', token);
        checkoutUrl.searchParams.set('utm_cart_token', token);
        checkoutUrl.searchParams.set('curRate', String(currencyRate));
        checkoutUrl.searchParams.set('theme', theme);
        if (shopifyDomain) checkoutUrl.searchParams.set('shopifyDomain', shopifyDomain);
        if (storefrontHost) checkoutUrl.searchParams.set('storefrontDomain', storefrontHost);
        if (rootRoute.length > 2) checkoutUrl.searchParams.set('subFolder', rootRoute.replace(/\//g, ''));
        appendTrackingParams(checkoutUrl.searchParams);

        var customerCommentField = document.querySelector('#Cart-note, textarea[name="note"][form="cart"]');
        var customerComment = clean(customerCommentField && customerCommentField.value);
        if (customerComment) {
          customerComment = customerComment.replace(/[;\r\n]+/g, ' ').replace(/\s+/g, ' ').slice(0, 500);
          checkoutUrl.searchParams.set('utm_comment', customerComment);
        }
      }

      processing = true;
      window.location.href = checkoutUrl.toString();
      return true;
    } catch (error) {
      processing = false;
      return false;
    }
  }

  function reset() {
    processing = false;
  }

  window.VskloCheklyCheckout = {
    processCheckout: processCheckout,
    reset: reset
  };
})();
